/**
 * Black-Friday-2026-Konfiguration nach dem gemeinsamen Vertrag (Plan 2.2).
 *
 * Die Aktionspreise selbst stehen NICHT hier: Sie kommen aus Saleor. Diese
 * Datei beschreibt nur Zeitraum, Kanäle, Auswahl, Mails und Grenzen. So gibt es
 * keine zweite Preisquelle, die von Saleor abweichen kann. Die Aktionsmengen je
 * Produkt liegen in der Tabelle `CampaignQuota`, damit sie ohne Deploy änderbar
 * sind.
 */

export interface BlackFridayCampaignConfig {
	key: "black-friday-2026";
	timeZone: "Europe/Berlin";
	utcOffset: "+01:00";
	dates: { earlyAccess: "2026-11-25"; publicStart: "2026-11-27"; endExclusive: "2026-12-01" };
	channels: ReadonlyArray<{ slug: string; currency: "EUR" | "USD"; discountPercent: number }>;
	productSlugs: readonly string[];
	signupSource: "black-friday-early-access";
	promotionName: "Black Friday 2026";
	priceOverrideReason: "Black Friday 2026 Früher Zugang";
	mails: ReadonlyArray<{
		key: "black-friday-2026-start" | "black-friday-2026-public" | "black-friday-2026-last-chance";
		sendFromUtc: string;
		sendUntilUtc: string;
		audience: "newsletter";
	}>;
}

/** D1 (Manfred, 26.10.): 20 % in `eu` und `us`, nur auf die Aktionsprodukte. */
const DISCOUNT_PERCENT = 20;

export const BLACK_FRIDAY_CONFIG: BlackFridayCampaignConfig = {
	key: "black-friday-2026",
	timeZone: "Europe/Berlin",
	// Der November liegt in Mitteleuropa bereits in der Winterzeit.
	utcOffset: "+01:00",
	dates: { earlyAccess: "2026-11-25", publicStart: "2026-11-27", endExclusive: "2026-12-01" },
	channels: [
		{ slug: "eu", currency: "EUR", discountPercent: DISCOUNT_PERCENT },
		{ slug: "us", currency: "USD", discountPercent: DISCOUNT_PERCENT },
	],
	productSlugs: ["retatrutide", "tirzepatid", "ghk-cu", "bpc-157", "glow-stack", "bac-water-10ml"],
	signupSource: "black-friday-early-access",
	promotionName: "Black Friday 2026",
	priceOverrideReason: "Black Friday 2026 Früher Zugang",
	// D7: alle drei Mails an alle Newsletter-Abonnenten mit gültiger Einwilligung,
	// 08:00 bzw. 10:00 Berlin, Fenster halboffen bis Mitternacht Ortszeit.
	mails: [
		{
			key: "black-friday-2026-start",
			sendFromUtc: "2026-11-25T07:00:00Z",
			sendUntilUtc: "2026-11-25T23:00:00Z",
			audience: "newsletter",
		},
		{
			key: "black-friday-2026-public",
			sendFromUtc: "2026-11-27T07:00:00Z",
			sendUntilUtc: "2026-11-27T23:00:00Z",
			audience: "newsletter",
		},
		{
			key: "black-friday-2026-last-chance",
			sendFromUtc: "2026-11-30T09:00:00Z",
			sendUntilUtc: "2026-11-30T23:00:00Z",
			audience: "newsletter",
		},
	],
};

/**
 * Ende des Willkommensgutscheins `5RABATT` (D15): am R1-Tag, 05.11.2026 00:00
 * Berlin, halboffen. Bis dahin trägt die Willkommensmail die Gutscheinkarte,
 * danach wirbt sie mit dem frühen Zugang. Der Gutschein selbst endet in Saleor
 * per Owner-Skript; dieser Wert steuert nur die Mail.
 */
export const WELCOME_VOUCHER_END_UTC = "2026-11-04T23:00:00.000Z";

/**
 * Ankündigungsbeginn (Manfred, 31.10.): vorher zeigt keine Fläche die Aktion,
 * kein Header-Link, kein Teaser, kein PDP-Banner, kein Mail-Hinweis auf den
 * frühen Zugang, und `/black-friday` antwortet mit notFound. 05.11.2026 00:00
 * Uhr Berlin (MEZ).
 */
export const BLACK_FRIDAY_ANNOUNCEMENT_START = "2026-11-04T23:00:00Z";

/**
 * Schlüssel im `CampaignSendLog` für die Bestätigung des frühen Zugangs. Jede
 * Adresse erhält sie höchstens einmal, als eigene Mail oder als Hinweis in der
 * Willkommensmail.
 */
export const BLACK_FRIDAY_CONFIRMATION_KEY = "black-friday-2026-confirmation";

/**
 * Alle Schlüssel, unter denen Black-Friday-Mails im `CampaignSendLog` stehen.
 * Sie gehen an jede angemeldete Adresse und zählen deshalb nicht in die
 * Kontaktpause anderer Kampagnen (72 h bei Retatrutide).
 */
export const BLACK_FRIDAY_SEND_LOG_KEYS: readonly string[] = [
	...BLACK_FRIDAY_CONFIG.mails.map((mail) => mail.key),
	BLACK_FRIDAY_CONFIRMATION_KEY,
];

/** Anker der Anmeldung, damit der Hero direkt dorthin verlinken kann. */
export const BLACK_FRIDAY_EARLY_ACCESS_ANCHOR = "frueher-zugang";
